import React, { useState, useEffect, useCallback } from "react";
import { Card, Spin, Button, Tag, Tabs, message, Typography, Descriptions, Space, Divider } from "antd";
import { ArrowLeftOutlined, ReloadOutlined, RobotOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, FileTextOutlined, QuestionCircleOutlined, BankOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import AssessmentReportDisplay from "../../Customer/Projects/AssessmentReportDisplay";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const PageWrapper = styled.div`
  padding: 24px;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #014c75;
    }
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .main-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .survey-responses {
    .response-item {
      margin-bottom: 16px;
      padding: 12px 16px;
      background: #f9f9f9;
      border-radius: 8px;
    }

    .response-label {
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }

    .response-value {
      color: #666;
    }
  }

  .org-link {
    color: #1890ff;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const surveyQuestionLabels = {
  projectType: "What best describes your project?",
  businessOutcome: "What are the primary business outcomes you want to achieve?",
  solutionFocus: "What is your main focus for this solution?",
  scaleScope: "What is the expected scale and scope?",
  technicalMaturity: "What is the current technical maturity of your organization?",
  internalCapability: "What is your internal capability?",
  keyChallenges: "What are the key challenges you're facing?",
  futureDirection: "What is your future direction?",
  engagementPreference: "What is your preferred engagement model?",
  costQualityBalance: "How do you balance cost vs quality?",
};

const AdminAssessmentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAssessment = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_CONST.GET_AI_PROJECT_ASSESSMENT}/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success) {
        setAssessment(data.assessment);
      } else {
        message.error(data.error || "Failed to fetch assessment");
      }
    } catch (error) {
      message.error("Failed to fetch assessment");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: "success", icon: <CheckCircleOutlined />, text: "Completed" },
      processing: { color: "processing", icon: <ClockCircleOutlined />, text: "Processing" },
      failed: { color: "error", icon: <ExclamationCircleOutlined />, text: "Failed" },
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Pending" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const renderSurveyResponses = () => {
    if (!assessment?.surveyResponses) return null;

    return (
      <div className="survey-responses">
        {Object.entries(assessment.surveyResponses).map(([key, value]) => {
          if (!value || (Array.isArray(value) && value.length === 0)) return null;
          return (
            <div key={key} className="response-item">
              <div className="response-label">{surveyQuestionLabels[key] || key}</div>
              <div className="response-value">
                {Array.isArray(value) ? (
                  <Space wrap>
                    {value.map((v, i) => (
                      <Tag key={i} color="blue">{v}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Text>{value}</Text>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <PageWrapper>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <Spin size="large" />
        </div>
      </PageWrapper>
    );
  }

  if (!assessment) {
    return (
      <PageWrapper>
        <Card>
          <div style={{ textAlign: "center", padding: 40 }}>
            <Text type="secondary">Assessment not found</Text>
            <br />
            <Button type="primary" onClick={() => navigate("/admin/assessments")} style={{ marginTop: 16 }}>
              Back to Assessments
            </Button>
          </div>
        </Card>
      </PageWrapper>
    );
  }

  const tabItems = [
    {
      key: "report",
      label: (
        <span>
          <FileTextOutlined />
          Assessment Report
        </span>
      ),
      children: assessment.status === "completed" && assessment.assessmentReport ? (
        <AssessmentReportDisplay 
          assessment={assessment} 
          onBack={() => navigate("/admin/assessments")}
          onCreateJob={() => {}}
          onViewAssessments={() => navigate("/admin/assessments")}
          onNavigate={(path) => navigate(path)}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          {assessment.status === "processing" ? (
            <>
              <Spin size="large" />
              <Text style={{ display: "block", marginTop: 16 }}>Assessment is being generated...</Text>
            </>
          ) : assessment.status === "failed" ? (
            <>
              <ExclamationCircleOutlined style={{ fontSize: 48, color: "#ff4d4f" }} />
              <Text style={{ display: "block", marginTop: 16 }}>Assessment generation failed</Text>
            </>
          ) : (
            <Text type="secondary">Report not available</Text>
          )}
        </div>
      ),
    },
    {
      key: "questions",
      label: (
        <span>
          <QuestionCircleOutlined />
          Survey Responses
        </span>
      ),
      children: (
        <div>
          <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Requirement Description">
              {assessment.requirementDescription}
            </Descriptions.Item>
            <Descriptions.Item label="Technologies">
              <Space wrap>
                {(assessment.technologies || []).map((tech, i) => (
                  <Tag key={i} color="blue">{tech}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          </Descriptions>
          <Divider>Survey Responses</Divider>
          {renderSurveyResponses()}
        </div>
      ),
    },
  ];

  return (
    <PageWrapper>
      <div className="page-header">
        <div className="header-left">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/admin/assessments")}>
            Back
          </Button>
          <h2>
            <RobotOutlined style={{ marginRight: 8 }} />
            Assessment Details
          </h2>
          {getStatusTag(assessment.status)}
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAssessment}>
          Refresh
        </Button>
      </div>

      <Card className="main-card" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={3}>
          <Descriptions.Item label="Assessment ID">{assessment.id}</Descriptions.Item>
          <Descriptions.Item label="Organization">
            <span 
              className="org-link" 
              onClick={() => navigate(`/admin/organization/${assessment.customerOrganization}`)}
            >
              <BankOutlined style={{ marginRight: 4 }} />
              {assessment.customerOrganization}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Created By">{assessment.userName}</Descriptions.Item>
          <Descriptions.Item label="Email">{assessment.userEmail}</Descriptions.Item>
          <Descriptions.Item label="Created At">{dayjs(assessment.createdAt).format("MMMM DD, YYYY h:mm A")}</Descriptions.Item>
          <Descriptions.Item label="Processed At">
            {assessment.processedAt ? dayjs(assessment.processedAt).format("MMMM DD, YYYY h:mm A") : "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="main-card">
        <Tabs items={tabItems} defaultActiveKey="report" />
      </Card>
    </PageWrapper>
  );
};

export default AdminAssessmentDetail;
